import { CommonModule, KeyValuePipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { map } from 'rxjs';

import { type AppNotification, type NotificationType } from '../../models/notification.model';
import { TranslateNotificationPipe } from '../../pipes/translate-notification.pipe';
import { NotificationHistoryService } from '../../services/notification-history.service';
import {
  type NotificationDetailsData,
  NotificationDetailsDialogComponent,
} from '../notification-details-dialog/notification-details-dialog.component';

@Component({
  selector: 'app-notification-panel',
  standalone: true,
  imports: [
    CommonModule,
    MatMenuModule,
    MatIconModule,
    MatButtonModule,
    MatBadgeModule,
    MatTooltipModule,
    TranslateNotificationPipe,
    MatDialogModule,
    KeyValuePipe,
    MatDividerModule,
  ],
  templateUrl: './notification-panel.component.html',
  styleUrls: ['./notification-panel.component.scss'],
})
export class NotificationPanelComponent {
  readonly pooledNotifications$ = inject(NotificationHistoryService).notifications$.pipe(
    map((notifications) => this.poolNotifications(notifications)),
  );
  readonly unreadCount$ = inject(NotificationHistoryService).unreadCount$;
  readonly iconForType: Record<NotificationType, string> = {
    success: 'check_circle',
    info: 'info',
    warning: 'warning',
    error: 'error',
  };

  private readonly notificationHistoryService = inject(NotificationHistoryService);
  private readonly dialog = inject(MatDialog);

  markAllAsRead(): void {
    this.notificationHistoryService.markAllAsRead();
  }

  clearAll(): void {
    this.notificationHistoryService.clearAll();
  }

  markAsRead(notificationId: number): void {
    this.notificationHistoryService.markAsRead(notificationId);
  }

  deleteNotification(notificationId: number): void {
    this.notificationHistoryService.deleteNotification(notificationId);
  }

  showDetails(details: string): void {
    this.dialog.open<NotificationDetailsDialogComponent, NotificationDetailsData>(
      NotificationDetailsDialogComponent,
      {
        data: { details },
      },
    );
  }

  private poolNotifications(notifications: AppNotification[]): Record<string, AppNotification[]> {
    const pools: Record<string, AppNotification[]> = {
      Today: [],
      Yesterday: [],
      Older: [],
    };

    notifications.forEach((notification) => {
      const notificationDate = new Date(notification.timestamp);
      if (this.isToday(notificationDate)) {
        pools['Today'].push(notification);
      } else if (this.isYesterday(notificationDate)) {
        pools['Yesterday'].push(notification);
      } else {
        pools['Older'].push(notification);
      }
    });

    if (pools['Today'].length === 0) delete pools['Today'];
    if (pools['Yesterday'].length === 0) delete pools['Yesterday'];
    if (pools['Older'].length === 0) delete pools['Older'];

    return pools;
  }

  private isToday(date: Date): boolean {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  }

  private isYesterday(date: Date): boolean {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return (
      date.getDate() === yesterday.getDate() &&
      date.getMonth() === yesterday.getMonth() &&
      date.getFullYear() === yesterday.getFullYear()
    );
  }
}
